import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Sparkles } from 'lucide-react';
import { Button } from '../ui/Button';
import { Input, Textarea } from '../ui/Input';
interface NewStreamModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (name: string, description: string) => void;
}
export function NewStreamModal({
  isOpen,
  onClose,
  onCreate
}: NewStreamModalProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (name.trim()) {
      onCreate(name.trim(), description.trim());
      setName('');
      setDescription('');
      onClose();
    }
  };
  return <AnimatePresence>
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" />

          {/* Modal */}
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        duration: 0.2
      }} className="fixed inset-4 sm:inset-auto sm:top-[5%] sm:left-1/2 sm:-translate-x-1/2 w-auto sm:w-full sm:max-w-md max-h-[90vh] overflow-y-auto bg-surface-900 border border-surface-600 rounded-xl shadow-2xl z-50">
            <form onSubmit={handleSubmit}>
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-surface-600">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-primary-500/10 flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-primary-400" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-text-primary">
                      Create New Stream
                    </h2>
                    <p className="text-sm text-text-secondary">
                      Start a new workflow or project
                    </p>
                  </div>
                </div>
                <button type="button" onClick={onClose} className="text-text-muted hover:text-text-primary transition-colors">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6 space-y-4">
                <Input label="Stream Name" placeholder="e.g., API Performance Optimization" value={name} onChange={e => setName(e.target.value)} autoFocus required />

                <Textarea label="Description (optional)" placeholder="What are you working on?" value={description} onChange={e => setDescription(e.target.value)} rows={3} />

                <div className="bg-surface-800/50 rounded-lg p-4 border border-surface-500">
                  <p className="text-xs text-text-secondary leading-relaxed">
                    <strong className="text-text-primary">Tip:</strong> Streams help
                    you organize complex work into manageable workflows. Each
                    stream can have multiple tasks that execute in parallel or
                    sequence.
                  </p>
                </div>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-end gap-3 p-6 border-t border-surface-600 bg-surface-900/50">
                <Button type="button" variant="ghost" onClick={onClose}>
                  Cancel
                </Button>
                <Button type="submit" disabled={!name.trim()}>
                  Create Stream
                </Button>
              </div>
            </form>
          </motion.div>
        </>}
    </AnimatePresence>;
}